import { z } from 'zod';
import { encryptedEnvelopeSchema, messageKindSchema, participantRoles } from './envelope.js';

/**
 * WebSocket protocol between browser and realtime service.
 * Documented in docs/realtime-protocol.md.
 *
 * Design rules:
 *  - Credentials are sent in the first frame, never in the URL. A URL would end
 *    up in proxy logs and browser history (concept §5).
 *  - Every payload carrying conversation content is an opaque encrypted
 *    envelope. The server never sees plaintext.
 */

/** Realtime endpoint path. Carries no identifiers and no credentials. */
export const REALTIME_PATH = '/rt';

/* ------------------------------------------------------------------ client → server */

export const helloFrameSchema = z.object({
  t: z.literal('hello'),
  /**
   * `owner-inbox` subscribes to account-level events (a visitor joined).
   * `conversation` subscribes to one conversation.
   */
  scope: z.enum(['owner-inbox', 'conversation']),
  /**
   * Opaque session credential. Authorises infrastructure access only.
   *
   * Optional for the owner: the owner session is an HttpOnly cookie, which the
   * browser attaches to the WebSocket upgrade request. Keeping it out of
   * JavaScript entirely is strictly better, so the client omits this field and
   * the server reads the cookie instead.
   */
  token: z.string().min(16).max(512).optional(),
  /** Required when scope is `conversation`. Opaque conversation identifier. */
  conversationId: z.string().uuid().optional(),
  /** Resume delivery after this sequence number. Omit to receive the backlog. */
  sinceSeq: z.number().int().nonnegative().optional(),
});

export const sendFrameSchema = z.object({
  t: z.literal('send'),
  /** Client-generated id, echoed in `ack` so the sender can match its own message. */
  clientId: z.string().min(1).max(64),
  kind: messageKindSchema,
  /** Encrypted content. Opaque to the server. */
  envelope: encryptedEnvelopeSchema,
  /**
   * For attachment kinds: the opaque object key previously issued by the API
   * and already uploaded as ciphertext.
   */
  objectKey: z.string().max(255).optional(),
});

export const typingFrameSchema = z.object({
  t: z.literal('typing'),
  active: z.boolean(),
});

export const translationStateFrameSchema = z.object({
  t: z.literal('translation-state'),
  /** Whether this participant has on-device translation active (concept §8). */
  active: z.boolean(),
  /** How translation is provided here, so the other party is not misled. */
  availability: z.enum(['browser', 'mock', 'unavailable']),
});

export const pingFrameSchema = z.object({ t: z.literal('ping') });

export const clientFrameSchema = z.discriminatedUnion('t', [
  helloFrameSchema,
  sendFrameSchema,
  typingFrameSchema,
  translationStateFrameSchema,
  pingFrameSchema,
]);
export type ClientFrame = z.infer<typeof clientFrameSchema>;

/* ------------------------------------------------------------------ server → client */

export interface MessageFrame {
  t: 'message';
  id: string;
  /** Monotonic per-conversation sequence number, assigned by the server. */
  seq: number;
  sender: (typeof participantRoles)[number];
  kind: z.infer<typeof messageKindSchema>;
  envelope: z.infer<typeof encryptedEnvelopeSchema>;
  objectKey?: string;
  /** Ciphertext byte length in object storage, for progress display. */
  objectSize?: number;
  createdAt: string;
}

export interface ReadyFrame {
  t: 'ready';
  scope: 'owner-inbox' | 'conversation';
  conversationId?: string;
  /** Server time, so clients can compute remaining life without trusting the device clock. */
  serverTime: string;
  /** When the conversation expires, ISO-8601. Absent for the owner inbox. */
  expiresAt?: string;
  /** Encrypted visitor display name, if the conversation has been claimed. */
  visitorName?: z.infer<typeof encryptedEnvelopeSchema>;
  /** Highest sequence number the server holds. */
  latestSeq?: number;
}

export interface AckFrame {
  t: 'ack';
  clientId: string;
  id: string;
  seq: number;
}

export interface PresenceFrame {
  t: 'presence';
  owner: boolean;
  visitor: boolean;
}

export interface TypingBroadcastFrame {
  t: 'typing';
  sender: (typeof participantRoles)[number];
  active: boolean;
}

export interface TranslationBroadcastFrame {
  t: 'translation-state';
  sender: (typeof participantRoles)[number];
  active: boolean;
  availability: 'browser' | 'mock' | 'unavailable';
}

/** A visitor claimed one of this owner's invitations. Carries no plaintext. */
export interface JoinedFrame {
  t: 'joined';
  conversationId: string;
  activatedAt: string;
  expiresAt: string;
  /** Encrypted visitor display name; readable only where the key is held. */
  visitorName: z.infer<typeof encryptedEnvelopeSchema>;
}

/** The conversation reached the end of its active period (concept §7). */
export interface ExpiredFrame {
  t: 'expired';
  conversationId: string;
  at: string;
}

export interface ErrorFrame {
  t: 'error';
  code: RealtimeErrorCode;
  message: string;
}

export interface PongFrame {
  t: 'pong';
  serverTime: string;
}

export type ServerFrame =
  | ReadyFrame
  | MessageFrame
  | AckFrame
  | PresenceFrame
  | TypingBroadcastFrame
  | TranslationBroadcastFrame
  | JoinedFrame
  | ExpiredFrame
  | ErrorFrame
  | PongFrame;

export const realtimeErrorCodes = [
  'unauthenticated',
  'forbidden',
  'not_found',
  'expired',
  'rate_limited',
  'invalid_frame',
  'payload_too_large',
  'internal',
] as const;
export type RealtimeErrorCode = (typeof realtimeErrorCodes)[number];

/** WebSocket close codes used by the service. 4000-4999 is the application range. */
export const CLOSE_CODES = {
  /** No valid `hello` frame arrived in time. */
  HELLO_TIMEOUT: 4000,
  /** Session credential missing, invalid, or revoked. */
  UNAUTHENTICATED: 4001,
  /** Valid credential, but not for this conversation. */
  FORBIDDEN: 4003,
  /** The conversation expired while the socket was open (concept §7). */
  EXPIRED: 4008,
  /** Frame violated the protocol. */
  PROTOCOL: 4002,
  /** Owner account suspended (concept §11). */
  SUSPENDED: 4009,
} as const;

/** Largest single realtime frame accepted, in bytes. */
export const MAX_FRAME_BYTES = 256 * 1024;
